import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import Guid from "common/values/guid/guid";
import NetworkConnectionForumAPIResponse from "./response-contracts/network-connection-forum-api-response";
import Forum from "messaging/entities/forum/forum";
import NetworkedUserInfoAPIResponse from "users/entities/user-network-connection/api/response-contracts/networked-user-info-api-response";
import NetworkedUserInfo from "users/entities/user-network-connection/networked-user-info";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import Session from "users/session/session";

export default class UserNetworkConnectionAPIService {
  private readonly _authHeaders: AxiosHeaders = new AxiosHeaders();
  private readonly _session: Readonly<Session>;

  constructor(session: Readonly<Session>) {
    this._session = session;
    if (!session.authToken?.value)
      throw new Error("Session must have an authToken to create a UserNetworkConnectionAPIService");
    this._authHeaders.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async getUserConnectionsInfo(abortController?: AbortController): Promise<NetworkedUserInfo[]> {
    const url = new URL("/users/network-connections", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this._authHeaders,
        signal: abortController?.signal
      });
      const connections: Array<NetworkedUserInfo> = [];
      response.data.forEach((connection: Partial<NetworkedUserInfoAPIResponse>) => {
        const connectionResponse = Object.assign(new NetworkedUserInfoAPIResponse(), connection);
        connections.push(connectionResponse.deserialize());
      });
      return connections;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        throw new Error(error.response.data);
      }
      if (error instanceof CanceledError) throw error;
      throw new UserNetworkConnectionAPIServiceError("getUserConnectionsInfo", error);
    }
  }

  async removeNetworkConnection(connectionId: Guid): Promise<void> {
    try {
      const url = new URL(`/users/network-connections/${connectionId}`, AttorneyHubAPIService.apiBaseUrl);
      await axios.delete(url.toString(), {
        headers: this._authHeaders
      });
      this._session.removeNetworkConnectionById(connectionId);

    } catch (error) {
      if (!(axios.isAxiosError(error))) {
        console.error(error);
        throw new Error(`A non-network-related error occurred while trying to remove network connection: ${error}`);
      }
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new NetworkConnectionNotFoundException(
            "Unable to remove network connection: not found."
          );
        case HttpStatusCode.BadRequest:
          throw new Error(error.response.data);
        default:
          throw new UserNetworkConnectionAPIServiceError("removeNetworkConnection", error);
      }
    }
  }

  async getChatForumForNetworkConnection(userId?: Guid, abortController?: AbortController): Promise<Forum | null> {
    if (!userId) throw new Error("User ID is required to get chat forum for network connection.");
    try {
      const url = new URL(`/users/network-chat/${userId}`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.get(url.toString(), {
        headers: this._authHeaders,
        signal: abortController?.signal
      });

      if (response.status === HttpStatusCode.NoContent) return null;

      const forumResponse: NetworkConnectionForumAPIResponse = Object.assign(
        new NetworkConnectionForumAPIResponse(), 
        response.data
      );
      return forumResponse.deserialize();
    } catch (error) {
      if (!(axios.isAxiosError(error))) {
        console.error(error);
        throw new Error(`A non-network-related error occurred while trying to get chat forum for network connection: ${error}`);
      }
      if (error?.response?.status === HttpStatusCode.BadRequest) {
        throw new Error(error.response.data);
      }
      if (error instanceof CanceledError) throw error;
      throw new UserNetworkConnectionAPIServiceError(
        "getChatForumForNetworkConnection", 
        error?.response?.data
      );
    }
  }

  async createChatForumForNetworkConnection(connectionId: Guid, abortController?: AbortController): Promise<Forum> {
    if (!connectionId) throw new Error("connection ID is required to create chat forum for network connection.");
    try {
      const url = new URL(`/users/network-chat/${connectionId}/create`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(url.toString(), {}, {
        headers: this._authHeaders,
        signal: abortController?.signal
      });

      const forumResponse: NetworkConnectionForumAPIResponse = Object.assign(new NetworkConnectionForumAPIResponse(), response.data);
      return forumResponse.deserialize();
    } catch (error) {
      if (!(axios.isAxiosError(error))) {
        console.error(error);
        throw new Error(`A non-network-related error occurred while trying to create chat forum for network connection: ${error}`);
      }
      if (error instanceof CanceledError) throw error;
      throw new UserNetworkConnectionAPIServiceError(
        "createChatForumForNetworkConnection", 
        error?.response?.data
      );
    }
  }
}

export class UserNetworkConnectionAPIServiceError extends Error {
  constructor(public method: string, public error: unknown) {
    super(`Error in UserNetworkConnectionAPIService.${method}: ${error}`);
    this.name = "UserNetworkConnectionAPIServiceError";
  }
}

export class NetworkConnectionNotFoundException extends Error { }
